import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useConnectGscMutation,
  useDeleteGscSourceMutation,
  useGetGscSelectionSessionQuery,
} from '../api/gscConnectionApi';
import type { GscConnectFailureReason } from '../SearchPerformanceAnalyticsEvents';
import { GSC_RECONNECT_PARAM } from '../SearchPerformanceRoutes';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import { toGscConnectionErrorCode, type GscConnectionError } from './GscConnectionErrors';
import {
  GSC_OAUTH_MESSAGE_TYPE,
  type GscConnectionParams,
  type GscOAuthMessage,
  watchGscOAuthPopup,
} from './GscOAuthBridge';
import useGscSyncState from './useGscSyncState';

function applyGscOAuthResult(
  result: Omit<GscOAuthMessage, 'type'>,
  onConnection: (connection: GscConnectionParams) => void,
  onFailure: (reason: GscConnectFailureReason, errorDetail?: string) => void
) {
  if (result.status === 'error') {
    onFailure(toGscConnectionErrorCode(result.errorCode, result.reason), result.reason);
  } else if (result.sessionId && result.sourceId) {
    onConnection({ sessionId: result.sessionId, sourceId: result.sourceId });
  } else {
    onFailure('malformed_result');
  }
}

export type GscConnectionFlow = {
  isStatusLoading: boolean;
  isConnecting: boolean;
  isGoingBack: boolean;
  /** Last failed attempt, rendered on the auth step until the next attempt. */
  error: GscConnectionError | null;
  connection: GscConnectionParams | null;
  isConnectedStep: boolean;
  /**
   * The connection has data behind it, so the dashboard is worth offering. Gates
   * the step-3 CTA — nothing acts on it automatically; see
   * `SearchPerformanceConnect`.
   */
  isSyncComplete: boolean;
  currentStep: number;
  /** Properties just picked, else the live GSC sources. */
  syncingDomainNames: string[];
  selectedPropertyCount: number;
  connect: () => Promise<void>;
  goBack: () => Promise<void>;
  completeActivation: (propertyUrls: string[]) => void;
  connectedAccountEmail: string | null;
  lostUrls: string[];
};

// Call once per page — `syncingDomains` is local state, so a second instance
// would derive its own `isSyncComplete`.
function useGscConnectionFlow(): GscConnectionFlow {
  const trackEvent = useTrackSearchPerformanceEvent();
  // Also read below, past the OAuth channels, for the unrelated `reconnect`
  // flag — the two happen to share this call, nothing more.
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectGsc, { isLoading: isConnecting }] = useConnectGscMutation();
  const [deleteGscSource, { isLoading: isDeletingSource }] = useDeleteGscSourceMutation();

  const {
    isStatusLoading,
    hasSyncedConnection,
    activeSources,
    connectedSource,
    connectedAccountEmail: statusAccountEmail,
    lostUrls,
    refetchStatus,
  } = useGscSyncState();

  const [connection, setConnection] = useState<GscConnectionParams | null>(null);
  const [syncingDomains, setSyncingDomains] = useState<string[]>([]);
  // Session is consumed on activate — skip the fetch on the sync step, but keep
  // the cache key so the account email already loaded still names the stepper.
  const { data: selectionSession } = useGetGscSelectionSessionQuery(connection?.sessionId ?? skipToken, {
    skip: syncingDomains.length > 0,
  });
  // Held rather than toasted: a toast carries no next step and is gone before a
  // customer can act on it.
  const [error, setError] = useState<GscConnectionError | null>(null);

  // Lets the popup watcher tell a silent close from a normal one.
  const gscResultReceivedRef = useRef(false);
  const cancelPopupWatchRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelPopupWatchRef.current?.(), []);

  // Same-tab channel: the callback lands the whole tab back here with its params.
  useEffect(() => {
    const sessionId = searchParams.get('gsc_selection_session_id');
    const sourceId = searchParams.get('source_id');
    const callbackStatus = searchParams.get('status');
    if (!sessionId && !sourceId && callbackStatus !== 'error') {
      return;
    }
    applyGscOAuthResult(
      {
        sessionId: sessionId ?? undefined,
        sourceId: sourceId ?? undefined,
        status: callbackStatus ?? undefined,
        reason: searchParams.get('reason') ?? undefined,
        errorCode: searchParams.get('error_code') ?? undefined,
      },
      (connection) => {
        trackEvent('Search Performance: Google Authenticated', { method: 'same_tab' });
        setError(null);
        setConnection(connection);
      },
      (reason, errorDetail) => {
        trackEvent('Search Performance: Connect Failed', {
          reason,
          method: 'same_tab',
          error_detail: errorDetail,
        });
        setError({ code: reason, detail: errorDetail });
      }
    );
    // `reconnect` is never in this list — it isn't one of the callback's own
    // params, so there is nothing here to strip. It survives this effect either
    // way, but not the same-tab OAuth round trip itself: the callback page
    // (`GscOAuthCallbackPage.tsx`) rebuilds the query string from just these
    // five keys before navigating back, so a reconnect that hits an error via
    // the popup-blocked fallback loses the flag on return. See the known gap in
    // `AGENTS.md`.
    const next = new URLSearchParams(searchParams);
    ['gsc_selection_session_id', 'source_id', 'status', 'reason', 'error_code'].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, trackEvent]);

  // Popup channel: the callback page postMessages the result to its opener.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data as GscOAuthMessage | null;
      if (!data || data.type !== GSC_OAUTH_MESSAGE_TYPE) {
        return;
      }
      gscResultReceivedRef.current = true;
      applyGscOAuthResult(
        data,
        (connection) => {
          trackEvent('Search Performance: Google Authenticated', { method: 'popup' });
          // A success can land after the close watcher already reported
          // popup_closed; the stale failure must not resurface on Back.
          setError(null);
          setConnection(connection);
        },
        (reason, errorDetail) => {
          trackEvent('Search Performance: Connect Failed', {
            reason,
            method: 'popup',
            error_detail: errorDetail,
          });
          setError({ code: reason, detail: errorDetail });
        }
      );
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [trackEvent]);

  const connect = async () => {
    trackEvent('Search Performance: Empty State CTA Clicked', { cta_type: 'connect_gsc' });
    setError(null);
    // Must open synchronously inside the click handler — once the async connect()
    // resolves the user gesture is spent and the blocker kills the popup.
    const popup = window.open('', 'gsc-oauth', 'width=520,height=640');
    try {
      const { authorizationUrl } = await connectGsc().unwrap();
      if (popup && !popup.closed) {
        popup.location.href = authorizationUrl;
        // The popup can dead-end without posting a result; without this watcher
        // the wizard would wait forever.
        gscResultReceivedRef.current = false;
        cancelPopupWatchRef.current?.();
        cancelPopupWatchRef.current = watchGscOAuthPopup(
          popup,
          () => gscResultReceivedRef.current,
          () => {
            trackEvent('Search Performance: Connect Failed', { reason: 'popup_closed', method: 'popup' });
            setError({ code: 'popup_closed' });
          }
        );
      } else {
        // Popup was blocked — fall back to a same-tab redirect.
        window.location.href = authorizationUrl;
      }
    } catch (err) {
      popup?.close();
      const httpStatus = typeof (err as { status?: unknown }).status === 'number'
        ? (err as { status: number }).status
        : (err as { originalStatus?: number }).originalStatus;
      // Believed unreachable: visibility reuses an abandoned PENDING GSC source
      // on create, and authorize only conflicts on an already-ACTIVE source,
      // which this flow never authorizes. Kept as a net — the contract isn't ours.
      if (httpStatus === 409) {
        const result = await refetchStatus();
        // GSC-scoped like the rest of the flow: a lone ACTIVE PRERENDER
        // companion is not a connection, and a companion id must never be the
        // one this cleanup deletes.
        const gscSources = result.data?.sources.filter((s) => s.type === 'GSC') ?? [];
        const hasActive = gscSources.some((s) => s.status === 'ACTIVE');
        if (!hasActive) {
          const stuck = gscSources.find((s) => s.status !== 'ACTIVE');
          if (stuck) {
            try { await deleteGscSource(stuck.id).unwrap(); } catch { /* best-effort */ }
          }
          trackEvent('Search Performance: Connect Failed', { reason: 'stuck_source' });
          setError({ code: 'stuck_source' });
        }
      } else {
        trackEvent('Search Performance: Connect Failed', { reason: 'request_failed' });
        setError({ code: 'request_failed' });
      }
    }
  };

  const goBack = async () => {
    // Narrowing only — the domain picker mounts solely when `connection` is set.
    if (!connection) {
      return;
    }
    try {
      await deleteGscSource(connection.sourceId).unwrap();
    } catch {
      // Silent on purpose: visibility reuses an abandoned PENDING GSC source on
      // the next attempt, so a failed delete costs the customer nothing.
    }
    setConnection(null);
  };

  const completeActivation = (propertyUrls: string[]) => setSyncingDomains(propertyUrls);

  // Presence, not value: the CTA sends `?reconnect=1`, but nothing should hinge
  // on the `1`. Read right here, next to the one term it affects, rather than
  // up with the OAuth channels — it has nothing to do with them.
  const isReconnectRequested = searchParams.get(GSC_RECONNECT_PARAM) !== null;

  // An existing active connection takes priority over in-progress OAuth state —
  // unless the customer asked to reconnect, which is the one case where they
  // came here *because* the live connection needs redoing. Only the entry point
  // knows that, so it says so in the URL (`GSC_RECONNECT_PARAM`).
  //
  // Standing this term down is enough to reach the auth step, and it cannot
  // strand anyone there even with the param still in the URL: the picker rides
  // on `connection` (which suppresses this branch anyway) and the sync step on
  // `syncingDomains` — the latter pinned by a test, since it is the one that
  // would make a reconnect impossible to finish.
  const showExistingConnection =
    !isStatusLoading &&
    connectedSource !== null &&
    syncingDomains.length === 0 &&
    !connection &&
    !isReconnectRequested;
  const isConnectedStep = syncingDomains.length > 0 || showExistingConnection;
  // Status must describe this activation: a previous account's cached sources
  // would otherwise skip the wait and fill the sync card. A subset check, not
  // a count: a live sibling this activation did not pair (it failed, or was
  // never selected) stays ACTIVE and must not hold the sync step open.
  const statusReflectsThisActivation =
    syncingDomains.length === 0 ||
    syncingDomains.every((url) => activeSources.some((source) => source.propertyUrl === url));
  const isSyncComplete = isConnectedStep && hasSyncedConnection && statusReflectsThisActivation;
  const syncingDomainNames =
    syncingDomains.length > 0 ? syncingDomains : activeSources.map((s) => s.propertyUrl as string);

  return {
    isStatusLoading,
    isConnecting,
    isGoingBack: isDeletingSource,
    error,
    connection,
    isConnectedStep,
    isSyncComplete,
    currentStep: isConnectedStep ? 2 : connection ? 1 : 0,
    syncingDomainNames,
    selectedPropertyCount: syncingDomains.length,
    connect,
    goBack,
    completeActivation,
    connectedAccountEmail: connection
      ? (selectionSession?.connectedAccountEmail ?? null)
      : statusAccountEmail,
    lostUrls,
  };
}

export default useGscConnectionFlow;
